import { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, UserRound } from 'lucide-react';
import { primaryButtonClass, secondaryButtonClass } from '@/shared/ui/formClasses';
import { STEP_ICONS } from './stepIcons';
import ContactStep from './ContactStep';
import FleetStep from './FleetStep';
import NeedsStep from './NeedsStep';
import ProfileStep from './ProfileStep';

const STEP_FIELDS = [ProfileStep, FleetStep, NeedsStep, ContactStep];

// Il primo campo con errore; di un gruppo di scelte, il primo pulsante.
const focusField = (field) => {
  const element = document.getElementById(`demo-${field}`);
  (element?.getAttribute('role') === 'group' ? element.querySelector('button') : element)?.focus();
};

/** Il modulo dello step corrente, con Indietro, Continua e Invia. */
export default function DemoRequestStepForm({ screen }) {
  const { step, steps, isLastStep, errors, submitting, submitError, onBack, onNext, onSubmit } =
    screen;
  const titleRef = useRef(null);
  const shownStep = useRef(step);

  // A ogni cambio di step il fuoco va al suo titolo, non all'apertura della pagina.
  useEffect(() => {
    if (step !== shownStep.current) titleRef.current.focus();
    shownStep.current = step;
  }, [step]);

  // Ogni verifica fallita porta il fuoco al primo campo da correggere.
  useEffect(() => {
    const [first] = Object.keys(errors);
    if (first) focusField(first);
  }, [errors]);
  const current = steps[step];
  const StepIcon = STEP_ICONS[step];
  const Fields = STEP_FIELDS[step];

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      aria-labelledby="demo-step-title"
      className="rounded-hero border border-default bg-default p-6 shadow-resting md:p-8"
    >
      <div className="flex flex-col gap-4 border-b border-subtle pb-6 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="inline-flex items-center gap-2 text-eyebrow text-link">
            <StepIcon aria-hidden className="size-3.5" />
            {current.label}
          </p>
          <h2
            id="demo-step-title"
            ref={titleRef}
            tabIndex={-1}
            className="mt-4 text-3xl font-semibold tracking-tight text-strong outline-hidden"
          >
            {current.title}
          </h2>
        </div>
        <div className="max-w-md">
          <p className="text-sm text-muted">{current.description}</p>
          <p className="mt-2 text-sm text-subtle">
            Bozza salvata automaticamente nel browser mentre compili il questionario.
          </p>
        </div>
      </div>

      <p className="mt-6 rounded-xl border border-info-muted bg-info-subtle px-4 py-3 text-sm text-default">
        Rispondi solo a ciò che sai già oggi: ci basta una fotografia chiara del contesto per
        impostare la demo giusta.
      </p>

      {submitError && (
        <p
          role="alert"
          className="mt-6 rounded-xl border border-danger-muted bg-danger-subtle px-4 py-3 text-sm text-danger"
        >
          {submitError}
        </p>
      )}

      <div className="mt-8 space-y-6">
        <Fields {...screen} />
      </div>

      <div className="mt-10 flex flex-col gap-3 border-t border-subtle pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-3 text-sm text-subtle">
          <UserRound aria-hidden className="size-4 shrink-0 text-placeholder" />
          Le risposte servono a preparare una demo sulla tua flotta.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          {step > 0 && (
            <button type="button" onClick={onBack} className={secondaryButtonClass}>
              <ArrowLeft aria-hidden className="size-3.5" />
              Indietro
            </button>
          )}
          {isLastStep ? (
            <button type="submit" disabled={submitting} className={primaryButtonClass}>
              {submitting ? 'Invio in corso…' : 'Invia richiesta'}
              <ArrowRight aria-hidden className="size-3.5" />
            </button>
          ) : (
            <button type="button" onClick={onNext} className={primaryButtonClass}>
              Continua
              <ArrowRight aria-hidden className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
